import React, { useEffect } from 'react';
import { IconButton, Modal, Paper, Stack } from '@mui/material';
import { Close } from '@mui/icons-material';
import { t } from 'i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import type { GetApiUnits200Item } from '@/shared/api/models';
import POSHeading from '@/components/POS/Common/POSHeading';
import POSInput from '@/components/POS/Common/POSInput';
import POSButton from '@/components/POS/Common/POSButton';

type UnitFormValues = {
    name: string;
    abbreviation: string;
};

interface UnitCreateEditModalProps {
    open: boolean;
    onClose: () => void;
    unit: GetApiUnits200Item | null;
    onSubmit: (values: UnitFormValues) => Promise<void> | void;
}

export default function UnitCreateEditModal({ open, onClose, unit, onSubmit }: UnitCreateEditModalProps) {
    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t('Customer.ADVJournalFieldNameError')).typeError(t('Customer.ADVJournalFieldNameError')),
        abbreviation: Yup.string().required(t('POS.UnitAbbreviationError')).typeError(t('POS.UnitAbbreviationError')),
    });

    const formik = useFormik<UnitFormValues>({
        initialValues: { name: '', abbreviation: '' },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                await onSubmit(values);
                resetForm();
                onClose();
            } catch (err) {
                // Intentionally keep the modal open if the caller rejects/throws.
                console.error('[UnitCreateEditModal] onSubmit failed:', err);
            }
        },
    });

    const handleClose = (reset = true) => {
        if (reset) formik.resetForm();
        onClose();
    };

    useEffect(() => {
        if (!open) return;

        formik.setValues({
            name: unit?.name ?? '',
            abbreviation: unit?.abbreviation ?? '',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, unit?.id]);

    useEffect(() => {
        if (open) return;
        formik.resetForm();
    }, [open]);

    return (
        <Modal
            open={open}
            onClose={!formik.isSubmitting ? () => handleClose(true) : undefined}
            keepMounted
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Paper
                sx={{
                    position: 'relative',
                    maxWidth: 600,
                    maxHeight: '80%',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 8,
                    py: 4,
                    px: 4,
                    minWidth: '40%',
                    minHeight: '20%',
                }}
            >
                <IconButton
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    onClick={() => handleClose(true)}
                    disabled={formik.isSubmitting}
                >
                    <Close />
                </IconButton>

                <POSHeading text={unit?.id ? t('POS.UpdateUnit') : t('POS.CreateUnit')} />

                <Stack spacing={3} sx={{ pt: 4 }}>
                    <Stack spacing={1}>
                        <POSHeading text={t('Common.Name')} fontSize={16} sx={{ fontWeight: 600 }} />
                        <POSInput
                            name="name"
                            value={formik.values.name}
                            onChange={(e) => formik.setFieldValue('name', e.target.value)}
                            onBlur={() => formik.setFieldTouched('name', true)}
                            placeholder={t('Common.Name')}
                            error={formik.touched.name && !!formik.errors.name}
                            helperText={formik.touched.name && formik.errors.name ? formik.errors.name : undefined}
                        />
                    </Stack>

                    <Stack spacing={1}>
                        <POSHeading text={t('POS.UnitAbbreviation')} fontSize={16} sx={{ fontWeight: 600 }} />
                        <POSInput
                            name="abbreviation"
                            value={formik.values.abbreviation}
                            onChange={(e) => formik.setFieldValue('abbreviation', e.target.value)}
                            onBlur={() => formik.setFieldTouched('abbreviation', true)}
                            placeholder={t('POS.UnitAbbreviation')}
                            error={formik.touched.abbreviation && !!formik.errors.abbreviation}
                            helperText={
                                formik.touched.abbreviation && formik.errors.abbreviation ? formik.errors.abbreviation : undefined
                            }
                        />
                    </Stack>

                    <POSButton
                        title={unit?.id ? t('Customer.SaveCh') : t('POS.CreateUnit')}
                        variant="save"
                        width={{ xs: '100%', md: 'auto' }}
                        sx={{ mx: 'auto', mt: 4 }}
                        onClick={formik.handleSubmit}
                        disabled={formik.isSubmitting}
                    />
                </Stack>
            </Paper>
        </Modal>
    );
}

